import { useTimezone } from '@/utils/hooks/useTimezone';
import dayjs from 'dayjs';
import { TaxRemittanceViewer } from 'deckard-ui/dist/components/TaxRemittanceViewer/TaxRemittanceViewer';
import {
  IPermitTOTPayment,
  Settings,
} from 'deckard-ui/dist/components/TaxRemittanceViewer/types';
import isEmpty from 'lodash/isEmpty';
import kebabCase from 'lodash/kebabCase';
import React, { useMemo } from 'react';
import { useTransientOccupancyTaxName } from '../../hooks/useTaxName';
import { License } from '../types';
import {
  gatherHistoricPaymentsPeriods,
  restrictToLatestYearsHistoricPeriods,
} from '../utils/helpers';
import { processPermitTOTPayment } from '../utils/process-payment';

interface Props extends Settings {
  paymentsData: License[];
  selectedLicenseId: number | undefined;
  selectedYear: number;
}

export function HistoricPaymentPeriods(props: Props): React.ReactElement {
  const timezone = useTimezone();

  const totAlias = useTransientOccupancyTaxName();

  const permitByLicenseId = useMemo(
    () =>
      props.paymentsData.find(
        (payments) =>
          payments.certificateInfo.licenseId === props.selectedLicenseId
      ) as License,
    // We cast the evaluated as "License" as the selected license ID will always exist due to "LicenseDropdown" usage
    [props.paymentsData, props.selectedLicenseId]
  );

  const paymentPeriods = useMemo(() => {
    // If this evaluates to true, then we will want to render 12 months of payment periods regardless of year.
    const selectedTimeFrameIsCurrentYear =
      dayjs().year() === props.selectedYear;

    const historicPaymentPeriods = gatherHistoricPaymentsPeriods(
      permitByLicenseId.paymentPeriods
    );

    if (selectedTimeFrameIsCurrentYear) {
      return restrictToLatestYearsHistoricPeriods(
        historicPaymentPeriods,
        permitByLicenseId.totPaymentPeriod
      );
    }

    return historicPaymentPeriods.filter(
      (paymentPeriod) =>
        dayjs(paymentPeriod.startDate).year() === props.selectedYear
    );
  }, [
    permitByLicenseId.paymentPeriods,
    permitByLicenseId.totPaymentPeriod,
    props.selectedYear,
  ]);

  const payments: {
    key: string;
    label: string;
    payment?: IPermitTOTPayment;
  }[] = useMemo(
    () =>
      paymentPeriods.flatMap(({ label, payments }) => {
        if (isEmpty(payments)) return { label, key: kebabCase(label) };
        /** Shallowly copy the payments array to avoid mutating the original list of payments per period. */
        const sortedPayments = [...payments].sort((a, b) =>
          dayjs(b.paymentDate).diff(dayjs(a.paymentDate))
        );
        return sortedPayments.map((payment) => ({
          label,
          payment: processPermitTOTPayment(
            timezone,
            payment,
            totAlias,
            props.reportingDetails
          ),
          key: `payment-${String(payment.paymentId)}-${payment.startDate}`,
        }));
      }),
    [paymentPeriods]
  );

  return (
    <div
      data-testid="property-tax-reporting-history"
      className="flex flex-col gap-4"
    >
      {payments.map(({ key, label, payment }) => (
        <TaxRemittanceViewer
          key={key}
          label={label}
          payment={payment}
          providers={props.providers}
          reportingDetails={props.reportingDetails}
          taxConfiguration={props.taxConfiguration}
        />
      ))}
    </div>
  );
}
